document.addEventListener('DOMContentLoaded', () => {
  const mainHeader = document.querySelector('[data-main-header]');

  if (mainHeader) {
    const updateHeaderState = () => {
      mainHeader.classList.toggle('shadow-sm', window.scrollY > 10);
    };

    updateHeaderState();
    window.addEventListener('scroll', updateHeaderState, { passive: true });
  }

  const initMobileMenu = () => {
    const trigger = document.querySelector('[data-mobile-menu-trigger]');
    const drawer = document.querySelector('[data-drawer]');
    const drawerFooter = document.querySelector('[data-drawer-footer]');
    const mobileHeader = document.querySelector('[data-mobile-header]');
    const tabs = [...document.querySelectorAll('[data-mobile-tab]')];
    const panels = [...document.querySelectorAll('[data-mobile-tab-panel]')];
    const hamburgerIcon = document.querySelector('[data-mobile-hamburger-icon]');
    const closeIcon = document.querySelector('[data-mobile-close-icon]');
    const investorNavbar = document.querySelector('[data-investor-navbar]');
    const desktopMedia = window.matchMedia('(min-width: 1024px)');

    if (!trigger || !drawer || tabs.length === 0 || panels.length === 0) return;

    const investorHome = investorNavbar?.parentElement;
    const investorAnchor = investorNavbar?.nextElementSibling ?? null;

    const setDrawerTop = () => {
      if (mobileHeader) {
        drawer.style.setProperty('--drawer-top', `${mobileHeader.getBoundingClientRect().bottom}px`);
      }
    };

    let isMenuOpen = false;
    let openIndex = null;

    const updateIcon = () => {
      hamburgerIcon?.classList.toggle('hidden', isMenuOpen);
      closeIcon?.classList.toggle('hidden', !isMenuOpen);
      trigger.setAttribute('aria-expanded', String(isMenuOpen));
    };

    const closeAllTabs = () => {
      openIndex = null;

      for (const tab of tabs) {
        tab.dataset.active = 'false';
        tab.setAttribute('aria-selected', 'false');
      }

      for (const panel of panels) {
        panel.classList.add('hidden');
      }
    };

    const openTab = (index) => {
      openIndex = index;

      for (const tab of tabs) {
        const isActive = Number(tab.dataset.mobileTabIndex) === index;
        tab.dataset.active = String(isActive);
        tab.setAttribute('aria-selected', String(isActive));
      }

      for (const panel of panels) {
        const isActive = Number(panel.dataset.mobileTabPanelIndex) === index;
        panel.classList.toggle('hidden', !isActive);
      }
    };

    const openMenu = () => {
      isMenuOpen = true;
      setDrawerTop();

      if (investorNavbar && drawerFooter) {
        drawerFooter.appendChild(investorNavbar);
        investorNavbar.setAttribute('data-mobile-open', 'true');
      }

      drawer.dataset.open = 'true';
      drawer.inert = false;
      document.body.classList.add('overflow-hidden');
      updateIcon();

      const firstTab = tabs[0];
      if (firstTab) {
        openTab(Number(firstTab.dataset.mobileTabIndex));
        firstTab.focus({ preventScroll: true });
      }
    };

    const closeMenu = () => {
      isMenuOpen = false;
      drawer.dataset.open = 'false';
      drawer.inert = true;
      document.body.classList.remove('overflow-hidden');

      if (investorNavbar) {
        investorNavbar.setAttribute('data-mobile-open', 'false');
        investorHome?.insertBefore(investorNavbar, investorAnchor);
      }

      closeAllTabs();
      updateIcon();
      // Focus would otherwise be left on an element inside the now-inert drawer.
      trigger.focus({ preventScroll: true });
    };

    for (const tab of tabs) {
      tab.addEventListener('click', () => {
        const index = Number(tab.dataset.mobileTabIndex);
        if (index === openIndex) {
          closeAllTabs();
        } else {
          openTab(index);
        }
      });
    }

    trigger.addEventListener('click', () => {
      if (isMenuOpen) {
        closeMenu();
      } else {
        openMenu();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && isMenuOpen) closeMenu();
    });

    desktopMedia.addEventListener('change', ({ matches }) => {
      if (matches && isMenuOpen) closeMenu();
    });

    window.addEventListener(
      'resize',
      () => {
        if (isMenuOpen) setDrawerTop();
      },
      { passive: true },
    );
  };

  const initMegaMenu = () => {
    const megaTriggers = [...document.querySelectorAll('[data-mega-trigger]')];
    const megaPanels = [...document.querySelectorAll('[data-mega-panel]')];

    if (megaTriggers.length === 0 || megaPanels.length === 0) return;

    let activeMegaIndex = null;
    let skipFocusOpen = false;

    const setExpanded = (item, isOpen) => {
      item.dataset.open = String(isOpen);
      item.querySelector('[data-mega-link]')?.setAttribute('aria-expanded', String(isOpen));
    };

    const showMega = (index) => {
      for (const item of megaTriggers) {
        setExpanded(item, Number(item.dataset.megaIndex) === index);
      }
      for (const panel of megaPanels) {
        panel.dataset.open = String(Number(panel.dataset.megaIndex) === index);
      }
    };

    const hideAllMega = () => {
      for (const item of megaTriggers) {
        setExpanded(item, false);
      }
      for (const panel of megaPanels) {
        panel.dataset.open = 'false';
      }
    };

    const revertMega = () => {
      if (activeMegaIndex !== null) {
        showMega(activeMegaIndex);
      } else {
        hideAllMega();
      }
    };

    for (const item of megaTriggers) {
      const index = Number(item.dataset.megaIndex);

      item.addEventListener('mouseenter', () => showMega(index));
      item.addEventListener('mouseleave', revertMega);
      item.addEventListener('focusin', () => {
        // Focus returned by Escape must not reopen the panel it just closed.
        if (skipFocusOpen) {
          skipFocusOpen = false;
          return;
        }
        showMega(index);
      });
      item.addEventListener('focusout', revertMega);

      const link = item.querySelector('[data-mega-link]');
      link?.addEventListener('click', (e) => {
        e.preventDefault();

        if (activeMegaIndex === index) {
          activeMegaIndex = null;
          hideAllMega();
        } else {
          activeMegaIndex = index;
          showMega(index);
        }
      });
    }

    document.addEventListener('click', (e) => {
      if (activeMegaIndex === null) return;
      if (!e.target.closest('[data-mega-trigger]')) {
        activeMegaIndex = null;
        hideAllMega();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const openItem = megaTriggers.find((item) => item.dataset.open === 'true');
      if (!openItem) return;
      activeMegaIndex = null;
      hideAllMega();
      // Keyboard users inside the panel go back to the nav item that opened it.
      if (openItem.contains(document.activeElement)) {
        skipFocusOpen = true;
        openItem.querySelector('[data-mega-link]')?.focus();
        skipFocusOpen = false;
      }
    });
  };

  initMobileMenu();
  initMegaMenu();
});
